import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Week from './pages/Week';
import Calendar from './pages/Calendar';
import Notes from './pages/Notes';
import Settings from './pages/Settings';
import Subscribe from './pages/Subscribe';
import SubscribeSuccess from './pages/SubscribeSuccess';
import AdminDashboard from './pages/AdminDashboard';
import Docs from './pages/Docs';
import Privacy from './pages/Privacy';
import TermsOfService from './pages/TermsOfService';
import VerifyEmail from './pages/VerifyEmail';
import OnboardingModal from './components/OnboardingModal';
import { Toaster } from './components/ui/sonner';
import InstallPrompt from './components/InstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import VerificationBanner from './components/VerificationBanner';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="flex justify-center items-center min-h-screen dark:bg-gray-900 text-gray-700 dark:text-gray-300">Loading...</div>;
  }
  return user ? children : <Navigate to="/login" />;
};

const AdminProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  if (!user.is_admin && user.subscription_status !== 'admin') {
    return <Navigate to="/app" />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  return !user ? children : <Navigate to="/app" />;
};

const AppContent = () => {
  const { user, refreshUser } = useAuth();
  const [voiceModalTrigger, setVoiceModalTrigger] = useState(0);
  const [exportModalTrigger, setExportModalTrigger] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check if user needs to see onboarding
  useEffect(() => {
    if (user && !user.has_seen_onboarding) {
      setShowOnboarding(true);
    }
  }, [user]);

  const handleOpenVoiceModal = () => {
    window.dispatchEvent(new CustomEvent('openVoiceModal'));
    setVoiceModalTrigger(prev => prev + 1);
  };

  const handleOpenExportModal = () => {
    window.dispatchEvent(new CustomEvent('openExportModal'));
    setExportModalTrigger(prev => prev + 1);
  };

  const handleOnboardingComplete = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/mark-onboarding-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setShowOnboarding(false);
        // Refresh user data to update has_seen_onboarding
        await refreshUser();
      }
    } catch (error) {
      console.error('Error marking onboarding complete:', error);
      setShowOnboarding(false);
    }
  };

  const handleOnboardingSkip = async () => {
    await handleOnboardingComplete(); // Same as complete
  };

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<PublicRoute><Auth /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><Auth /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        
        {/* Public Documentation Pages */}
        <Route path="/docs" element={<Docs />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/termsofuse" element={<TermsOfService />} />

        {/* Protected App Routes */}
        <Route path="/app" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/week" element={<ProtectedRoute><Week /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
        <Route path="/subscribe/success" element={<ProtectedRoute><SubscribeSuccess /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />

        {/* Legacy route redirects */}
        <Route path="/auth" element={<Navigate to="/login" replace />} />
      </Routes>

      {/* Onboarding Modal */}
      {user && showOnboarding && (
        <OnboardingModal
          open={showOnboarding}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      <Toaster position="top-center" richColors />
    </>
  );
};

function App() {
  useEffect(() => {
    // STEP 3: Temporarily unregister service worker to ensure fresh JS
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => {
          console.log('[STEP 3] Unregistering service worker');
          reg.unregister();
        });
      });
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            {/* Offline Indicator */}
            <OfflineIndicator />
            <VerificationBanner />
            
            <AppContent />
            
            {/* Install Prompt */}
            <InstallPrompt />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
